import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  DrawingCompletionNotifier,
  DrawingNotification,
} from 'src/app/services/drawings/drawing-completion-notifier.service';

/**
 * Global stack of "drawing ready" notifications, top-right.
 *
 * Mounted once in AppComponent so it appears wherever the user is — processing a large set
 * takes minutes and nobody sits on the drawings screen for that long.
 *
 * Built as a plain component rather than an ngx-toastr custom toast. Subclassing toastr's
 * Toast silently dropped its host bindings (@Component metadata is not inherited), which
 * stripped the card styling entirely and left text floating over the page header. Owning the
 * markup avoids that whole class of problem and matches CaptureToastComponent, the pattern
 * already used elsewhere in the app.
 */
@Component({
  standalone: false,
  selector: 'app-drawing-notifications',
  templateUrl: './drawing-notifications.component.html',
  styleUrls: ['./drawing-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawingNotificationsComponent implements OnInit, OnDestroy {
  notifications: DrawingNotification[] = [];

  private subscription: Subscription | null = null;

  constructor(
    private notifier: DrawingCompletionNotifier,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription = this.notifier.notifications$.subscribe((list) => {
      // The feed originates in a Firebase RTDB callback, which FirebaseSignalProvider does not
      // wrap in NgZone — so without re-entering the zone Angular schedules no change detection
      // and, being OnPush, this view never repaints. The card lands in the array and nothing
      // appears on screen. The drawings list gets away with it only because it uses default
      // change detection and other interactions happen to tick it.
      this.zone.run(() => {
        this.notifications = list;
        this.cdr.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /** Stable identity so an unrelated push does not re-animate the whole stack. */
  trackById(_index: number, item: DrawingNotification): string {
    return item.id;
  }

  view(item: DrawingNotification): void {
    this.notifier.view(item);
  }

  dismiss(item: DrawingNotification): void {
    this.notifier.dismiss(item.id);
  }
}

